'use client'

import { useState } from 'react'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type InputProps = React.ComponentProps<typeof Input>

/**
 * Password input with an inline show/hide eye toggle.
 *
 * Same API surface as ``<Input type="password" />`` so callers can swap in
 * without rewiring value / onChange / disabled / minLength / maxLength /
 * autoFocus. The toggle is purely visual: the input itself is the source
 * of truth and the form submission still receives the plaintext value.
 *
 * Accessibility: the toggle is a real ``<button type="button">`` with an
 * aria-label so screen readers announce its purpose; it intentionally does
 * not auto-revert on blur so the user can verify what they typed without
 * losing focus.
 */
export function PasswordInput({ className, ...inputProps }: Omit<InputProps, 'type'>) {
  const t = useTranslations('Share.passwordInput')
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <Input
        {...inputProps}
        type={visible ? 'text' : 'password'}
        // Pad the right so the typed value never slides under the toggle button.
        className={cn('pe-9', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={inputProps.disabled}
        aria-label={visible ? t('hide') : t('show')}
        title={visible ? t('hide') : t('show')}
        className="text-muted-foreground hover:text-foreground absolute top-1/2 end-1.5 -translate-y-1/2 rounded p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        // Block the button from stealing focus on click — focus stays in the
        // input so the user can keep typing without an extra Tab.
        onMouseDown={(e) => e.preventDefault()}
      >
        {visible ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
      </button>
    </div>
  )
}
